import { Option } from 'fp-ts/lib/Option';
import { Dispatch, useEffect, useMemo, useReducer, useState } from 'react';
import urlJoin from 'url-join';
import config from '../../config';
import { GroupedResults } from '../../lib/dto';
import captureError from '../captureError';
import { RemoteData } from '../RemoteData';
import { RefreshToken } from '../useRefresh';
import * as cache from './cache';
import fetchResults from './fetchResults';
import { Action, initialState, LoadedState, reducer } from './model';
import useLimit from './useLimit';

type HookResult = RemoteData<
  LoadedState & {
    loadMore: (args: {
      index: GroupedResults['Specific'];
      offset: number;
    }) => Promise<void>;
  }
>;

export default function useSearchResults({
  q,
  countryCode,
  refreshToken,
}: {
  q: Option<string>;
  countryCode: Option<string>;
  refreshToken?: RefreshToken;
}): HookResult {
  const [state, dispatch] = useReducer(reducer, initialState);

  const limit = useLimit();

  const url = useMemo(
    () =>
      urlJoin(
        config.searchUri,
        `?limit=${limit}`,
        q.map(qv => `?q=${qv}`).getOrElse(''),
        countryCode.map(cc => `?country=${cc}`).getOrElse('')
      ),
    [limit, q, countryCode]
  );

  const [abortController, setAbortController] = useState(new AbortController());

  // Persist the results to session cache every time they change
  useEffect(() => {
    if (state.type === 'loaded') cache.setResults(state.data.results);
  }, [state]);

  useEffect(() => {
    if (q.isNone()) {
      dispatch({ type: 'reset' });
      return;
    }

    const abortController = new AbortController();
    setAbortController(abortController);

    function cleanUp() {
      abortController.abort();
      dispatch({ type: 'reset' });
    }

    const cachedResults = cache.getResults();
    if (cachedResults.isRight()) {
      dispatch({ type: 'loaded', payload: cachedResults.value });
      return cleanUp;
    }

    dispatch({ type: 'loading' });

    (async () => {
      try {
        const results = await fetchResults(url, {
          signal: abortController.signal,
        });
        dispatch({ type: 'loaded', payload: results });
      } catch (error) {
        if (abortController.signal.aborted) return;
        captureError(error);
        dispatch({ type: 'error', payload: error });
      }
    })();

    return cleanUp;
  }, [q, countryCode, url, refreshToken]);

  if (state.type === 'loaded') {
    const loadMore = getLoadMore({
      url,
      dispatch,
      abortSignal: abortController.signal,
    });
    return { type: 'loaded', data: { ...state.data, loadMore } };
  }

  return state;
}

function getLoadMore({
  url,
  dispatch,
  abortSignal,
}: {
  url: string;
  dispatch: Dispatch<Action>;
  abortSignal: AbortSignal;
}) {
  return async function loadMore({
    index,
    offset,
  }: {
    index: GroupedResults['Specific'];
    offset: number;
  }) {
    try {
      dispatch({ type: 'pagination/loading', payload: index });

      const results = await fetchResults(
        urlJoin(url, `?i=${index}`, `?offset=${offset}`),
        { signal: abortSignal }
      );

      dispatch({ type: 'pagination/loaded', payload: { index, results } });
    } catch (e) {
      if (abortSignal.aborted) return;
      captureError(e);
      dispatch({ type: 'pagination/error', payload: index });
    }
  };
}
